import { useMemo, useRef, useState } from "react";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { SupplyType } from "../../types"
import { Box } from "@chakra-ui/react";
import moment from "moment";
import { SupplyLinkRenderer } from "../../components/CellRenderers";
import { caseInsensitiveComparator } from "../../util/AgGridFunctions";

type EventCalendarProps = {
  suppliesData: SupplyType[] | undefined
  selectedDates: Date[]
}

function CEDGrid({suppliesData, selectedDates}: EventCalendarProps) {

  // Column Definitions: Defines the columns to be displayed.
  const [colDefs] = useState<ColDef<SupplyType>[]>([
    { field: "cedgroup", headerName: "End Dates", hide: true, rowGroup: true, sort: "asc", sortIndex: 0, comparator: (a, b) => {
      // puts ooc to top and unknown to bottom
      if (a==="Out of Contract" || b==="Unknown") return -1;
      if (b==="Out of Contract" || a==="Unknown") return 1;
      return (moment(a, 'DD/MM/YYYY') > moment(b, 'DD/MM/YYYY')) ? 1 : -1;
    }},
    { field: "account_name", headerName: "Account Name", sort: "asc", sortIndex: 0, minWidth: 150 },
    { field: "site_group", headerName: "Site Group", sort: "asc", hide: true, sortIndex: 2, minWidth: 150 },
    { field: "site_address", headerName: "Site Address", sort: "asc", sortIndex: 3 },
    { field: "supply_type", headerName: "Supply Type", sort: "asc", sortIndex: 4, width: 110 },
    { field: "supply_subtype", headerName: "Supply Subtype", sort: "asc", hide: true, sortIndex: 5 },
    { field: "supply_verbose", headerName: "Supply Number", sort: "asc", sortIndex: 6, cellRenderer: SupplyLinkRenderer, minWidth: 300, cellStyle: {overflow: "visible"} },  
    { field: "supply_supplier", headerName: "Current Supplier" },
    { field: "contract_end_date", headerName: "Contract End Date", sort: "asc", hide: true },
    { field: "eb_currentconsumption", headerName: "Current Consumption", hide: true },
    { field: "supply_number", headerName: "Supply Number Short", hide: true },
    { field: "rollingcontract", headerName: "Out of Contract?", hide: true  }, 
    { field: "eb_meterserialnumber", headerName: "Meter Serial Number", hide: true  },
  ]);

  const defaultColDef: ColDef = useMemo(() => ({
    filter: true,
    enableRowGroup: true,
    enableValue: true,
    comparator: caseInsensitiveComparator,
  }), []);

  async function setFilterModelRefresh(gridRef: React.RefObject<AgGridReact<any>>, selectedDates: Date[]){
    const selectedDatesFilter = (selectedDates.length > 0) ? selectedDates.map((date: Date) => moment(date).format("YYYY-MM-DD")) : null;
    await gridRef.current?.api.setColumnFilterModel('contract_end_date', { values: selectedDatesFilter });
    gridRef.current?.api.onFilterChanged(); // ag grid needs to force filter refresh
  }

  //filter by selected dates in calendar
  const gridRef = useRef<AgGridReact>(null);
  setFilterModelRefresh(gridRef, selectedDates) // need to use async function to await setColumnFilterModel on first load

  return(
    <Box w="100%" h="100%">
      <div
      className="ag-theme-quartz" // applying the Data Grid theme
      style={{ height: '400px', width: '100%' }} // the Data Grid will fill the size of the parent container
      >
      <AgGridReact<SupplyType>
          rowData={suppliesData}
          columnDefs={colDefs}
          groupDisplayType={'groupRows'}
          rowGroupPanelShow={"always"}
          defaultColDef={defaultColDef}
          autoSizeStrategy={{type: 'fitGridWidth'}}
          ref={gridRef}
          suppressCsvExport={true}
          suppressExcelExport={true}
          groupDefaultExpanded={(selectedDates.length>0)?1:0} // expand groups after dates selected but minimise when not
          //domLayout={"autoHeight"} // !!! this can cause performance issues for large dataset - remove when we have finalised layout and can set fixed height
          groupAllowUnbalanced
      />
    </div>
  </Box>
  )

}

export default CEDGrid